import { useAuth } from 'context/AuthContext';
import { ROUTES } from 'constants/routes';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

const COOLDOWN_DAYS = 21;
const MAX_DISMISSALS = 3;
const SAVED_EVENTS_MILESTONE = 3;
const TRIGGER_DELAY_MS = 1200;

const STORAGE_KEYS = {
  lastShownAt: 'openmat:donationDialogLastShownAt',
  dismissCount: 'openmat:donationDialogDismissCount',
  supported: 'openmat:donationDialogSupported',
};

// Auth flows and the admin area are never appropriate moments to interrupt
// with a donation ask.
const EXCLUDED_PATHS = [ROUTES.login, ROUTES.signup, ROUTES.resetPassword, ROUTES.verifyEmail];

function isExcludedPath(pathname) {
  return EXCLUDED_PATHS.includes(pathname) || pathname.startsWith(ROUTES.admin.hub);
}

function canShowDialog() {
  if (localStorage.getItem(STORAGE_KEYS.supported) === 'true') return false;

  const dismissCount = parseInt(localStorage.getItem(STORAGE_KEYS.dismissCount) || '0', 10);
  if (dismissCount >= MAX_DISMISSALS) return false;

  const lastShownAt = localStorage.getItem(STORAGE_KEYS.lastShownAt);
  if (lastShownAt) {
    const daysSinceShown = (Date.now() - new Date(lastShownAt).getTime()) / (1000 * 60 * 60 * 24);
    if (daysSinceShown < COOLDOWN_DAYS) return false;
  }

  return true;
}

export const DonationContext = createContext({
  isOpen: false,
  openDialog: () => {},
  closeDialog: () => {},
});

export function useDonation() {
  return useContext(DonationContext);
}

export function DonationProvider({ children }) {
  const { user, isAdmin, userProfile } = useAuth();
  const location = useLocation();

  const [isOpen, setIsOpen] = useState(false);

  const openDialog = useCallback(() => {
    if (!user || user.isAnonymous || isAdmin) return undefined;
    if (isOpen) return undefined;
    if (!canShowDialog()) return undefined;

    const timeoutId = setTimeout(() => {
      setIsOpen(true);
      localStorage.setItem(STORAGE_KEYS.lastShownAt, new Date().toISOString());
    }, TRIGGER_DELAY_MS);

    return () => clearTimeout(timeoutId);
  }, [user, isAdmin, isOpen]);

  const closeDialog = useCallback((outcome = 'dismiss') => {
    setIsOpen(false);

    if (outcome === 'supported') {
      localStorage.setItem(STORAGE_KEYS.supported, 'true');
      return;
    }

    const dismissCount = parseInt(localStorage.getItem(STORAGE_KEYS.dismissCount) || '0', 10);
    localStorage.setItem(STORAGE_KEYS.dismissCount, String(dismissCount + 1));
  }, []);

  useEffect(
    function triggerDonationDialog() {
      if (!user || user.isAnonymous || isAdmin) return undefined;
      if (isExcludedPath(location.pathname)) return undefined;

      const isBookingSuccessPage = location.pathname === ROUTES.bookingSuccess();
      const reachedSavedEventsMilestone =
        (userProfile?.savedEvents?.length || 0) >= SAVED_EVENTS_MILESTONE;

      if (!isBookingSuccessPage && !reachedSavedEventsMilestone) return undefined;

      return openDialog();
    },
    [user, isAdmin, userProfile, location, openDialog]
  );

  return (
    <DonationContext.Provider
      value={{
        isOpen,
        openDialog,
        closeDialog,
      }}
    >
      {children}
    </DonationContext.Provider>
  );
}
